import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import recentChatLogo from 'assets/images/recent-chat-logo.svg';
import { SidebarCollapsibleSection } from 'components/blocks/layout/sidebar-section';
import { SidebarTreeItem } from 'components/blocks/layout/sidebar-items';
import { ProjectFolderIcon } from 'components/blocks/layout/project-folder-icon';
import { CreateAgentIcon } from 'components/blocks/layout/icons/create-agent-icon';
import {
    SIDEBAR_NAV_ICON_SIZE,
    SIDEBAR_NAV_ICON_STROKE,
    sidebarNavIconClass,
} from 'components/blocks/layout/sidebar-nav-icon';
import { useSidebar } from 'components/ui/sidebar';
import { getConversationDisplayTitle } from 'utils/chat-utilities';
import { recentChatVisual } from 'features/chat/utils/conversation-sidebar-visual';
import { Draggable } from 'features/sidebar/Draggable';
import { AgentSummary } from 'features/chat/agent-chat-module/types/agent.service.type';
import { BuddyFace } from 'config/buddy/utils';
import { getOnboardingRecentChatPath } from 'features/onboarding/onboarding-journey';
import { useCommonStore } from 'state/store/common/useCommonStore';
import { useOnboardingStore } from 'state/store/onboarding/use-onboarding-store';

interface IndividualChatsSectionProps {
    conversations: any[];
    isSidebarLoading: boolean;
    renderRightAction: (id: string, title: string) => React.ReactNode;
    variant?: 'default' | 'section';
    agents?: AgentSummary[];
}

function RecentChatLogo({ logoUrl }: { logoUrl?: string | null }) {
    const [logoError, setLogoError] = useState(false);
    const src = logoUrl?.trim();

    useEffect(() => {
        setLogoError(false);
    }, [src]);

    if (src && !logoError) {
        return (
            <img
                src={src}
                alt=""
                className="size-5 shrink-0 rounded-full object-cover"
                width={20}
                height={20}
                onError={() => setLogoError(true)}
            />
        );
    }

    return (
        <CreateAgentIcon
            width={SIDEBAR_NAV_ICON_SIZE}
            height={SIDEBAR_NAV_ICON_SIZE}
            strokeWidth={SIDEBAR_NAV_ICON_STROKE}
            className={sidebarNavIconClass}
        />
    );
}

function OnboardingChatMark() {
    return (
        <span className="relative size-5 shrink-0 overflow-hidden rounded-full">
            <span className="absolute left-1/2 top-1/2 block size-[30px] -translate-x-1/2 -translate-y-1/2">
                <BuddyFace haloId="onboarding-recent-chat" />
            </span>
        </span>
    );
}

function RecentChatMark({ item, agentLogoUrl }: { item: any; agentLogoUrl?: string | null }) {
    const visual = recentChatVisual(item);

    if (visual.kind === 'agent-logo') {
        return <RecentChatLogo logoUrl={visual.logoUrl} />;
    }

    if (visual.kind === 'agent-fallback') {
        return <RecentChatLogo logoUrl={agentLogoUrl} />;
    }

    if (visual.kind === 'project') {
        return <ProjectFolderIcon color={visual.color} />;
    }

    return (
        <img
            src={recentChatLogo}
            alt=""
            className="size-5 shrink-0"
            width={SIDEBAR_NAV_ICON_SIZE}
            height={SIDEBAR_NAV_ICON_SIZE}
        />
    );
}

export const IndividualChatsSection = ({
    conversations,
    isSidebarLoading,
    renderRightAction,
    variant = 'section',
    agents,
}: IndividualChatsSectionProps) => {
    const { pathname } = useLocation();
    const { t } = useTranslation();
    const { isMobile, setOpenMobile } = useSidebar();
    const navigate = useNavigate();
    const userId = useCommonStore((state) => state.user?.id);
    // Subscribed so the row updates when cloud progress from another device loads.
    const onboardingProgress = useOnboardingStore((state) =>
        userId ? state.progressByUser[userId] : undefined
    );
    const onboardingPath = getOnboardingRecentChatPath(onboardingProgress);

    const chatList = conversations ?? [];
    const onboardingChatPath = onboardingPath?.split('?')[0] ?? '';

    return (
        <SidebarCollapsibleSection
            title={t("INDIVIDUAL_CHATS")}
            variant={variant}
            defaultOpen
        >
            <div className="flex flex-col gap-1">
                {onboardingPath && (
                    <SidebarTreeItem
                        label={t('ONBOARDING_AND_WELCOME', { defaultValue: 'Onboarding and welcome' })}
                        iconElement={<OnboardingChatMark />}
                        variant={variant}
                        isActive={
                            onboardingPath === '/onboarding'
                                ? pathname === '/onboarding'
                                : pathname.startsWith(onboardingChatPath)
                        }
                        onClick={() => {
                            if (isMobile) setOpenMobile(false);
                            navigate(onboardingPath);
                        }}
                    />
                )}
                {chatList.length === 0 && !isSidebarLoading && !onboardingPath && (
                    <div className="py-1 px-2 text-center">
                        <p className="text-xs text-muted-foreground italic mb-2">
                            {t('NO_INDIVIDUAL_CHAT_YET') || 'No individual chats yet'}
                        </p>
                    </div>
                )}
                {chatList.map((item: any) => {
                    const displayTitle = getConversationDisplayTitle(
                        item.ConversationTitle,
                        t('NEW_CHAT')
                    );
                    // A chat started this session has no logo_url yet — read it off the agent.
                    const agentLogoUrl = item.AgentId
                        ? agents?.find((agent) => agent.id === item.AgentId)?.logo_url
                        : undefined;

                    return (
                    <Draggable key={item.ConversationId} id={item.ConversationId}>
                        <SidebarTreeItem
                            label={displayTitle}
                            iconElement={<RecentChatMark item={item} agentLogoUrl={agentLogoUrl} />}
                            variant={variant}
                            isActive={pathname.includes(item.ConversationId)}
                            onClick={() => {
                                if (isMobile) setOpenMobile(false);
                                navigate(`/chat/${item.ConversationId}`);
                            }}
                            rightAction={renderRightAction(item.ConversationId, displayTitle)}
                        />
                    </Draggable>
                    );
                })}
                {isSidebarLoading && (
                    <div className="flex h-10 items-center justify-center py-2">
                        <Loader2 className="h-4 w-4 animate-spin text-grey-400" />
                    </div>
                )}
            </div>
        </SidebarCollapsibleSection>
    );
};
